import React, { useState, useEffect } from "react";
import { Dialog, DialogPanel, DialogBackdrop } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../form/Button";
import { classNames } from "../../helpers/classNames";

const ConfirmationPopup = ({
  isOpen,
  setIsOpen = () => {},
  getActionValue = () => {},
  title,
  message,
  confirmButtonLabel,
  cancelButtonLabel,
  setLoading = () => {},
  loading = false,
}) => {
  const closeModal = () => {
    setIsOpen(false);
    setLoading(false);
    getActionValue({
      button_type: "close",
      row_id: "",
      current_status: "",
    });
  };

  const onSubmit = () => {
    getActionValue({
      button_type: "confirm",
      row_id: "",
      current_status: "",
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog open={isOpen} onClose={() => {}} className="relative z-50">
          {/* Backdrop with motion */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-25"
          />

          {/* Dialog positioning */}
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <DialogPanel
                as={motion.div}
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{
                  duration: 0.25,
                  ease: [0.4, 0.0, 0.2, 1],
                  type: "spring",
                  damping: 25,
                  stiffness: 300,
                }}
                className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-2 text-left align-middle shadow-xl relative"
              >
                {/* Close button with hover animation - FIXED */}
                <motion.div
                  className="absolute top-3 right-3 z-10"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <button
                    onClick={closeModal}
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    {/* Using × symbol as fallback instead of Font Awesome */}
                    <span className="text-lg leading-none">×</span>
                  </button>
                </motion.div>

                {/* Dialog content with stagger animation */}
                <motion.div
                  className={classNames("md:py-2 py-2 md:px-6 px-4")}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.2 }}
                >
                  <div className="flex flex-col justify-center items-center gap-2">
                    <motion.div
                      className="flex justify-start w-full items-center"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15, duration: 0.2 }}
                    >
                      <motion.i
                        className="fa-regular fa-exclamation-triangle text-yellow-600 rounded-full mr-2 text-2xl"
                        initial={{ rotate: -180, scale: 0 }}
                        animate={{ rotate: 0, scale: 1 }}
                        transition={{
                          delay: 0.2,
                          duration: 0.3,
                          type: "spring",
                        }}
                      />
                      <div className="text-lg font-bold pl-1">
                        {title ? title : "Confirmation"}
                      </div>
                    </motion.div>

                    <motion.hr
                      className="w-full border-1 border-slate-400"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ delay: 0.25, duration: 0.3 }}
                    />

                    <motion.div
                      className="flex pt-6 text-sm text-gray-700 font-semibold text-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.2 }}
                    >
                      {message ? (
                        <p>{message}</p>
                      ) : (
                        <p>
                          {
                            "Reminder: Input all necessary information in the Position Competencies window."
                          }
                        </p>
                      )}
                    </motion.div>

                    <motion.div
                      className="flex items-center gap-4 mt-3"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.2 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button
                          buttonLabelClasses=""
                          buttonType={"button"}
                          buttonIcon={"fa-solid fa-xmark"}
                          buttonIconPosition={"left"}
                          buttonLabel={
                            cancelButtonLabel ? cancelButtonLabel : "Cancel"
                          }
                          buttonClasses={
                            "!bg-rose-600 !border-transparent hover:!bg-rose-600/80 !rounded-lg"
                          }
                          buttonFunction={closeModal}
                          buttonHasLink={false}
                        />
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button
                          buttonClasses={
                            "!bg-primary !border-transparent !text-base !rounded-lg !py-2 !px-6 !text-white  hover:!bg-primary/80"
                          }
                          buttonLabelClasses=""
                          buttonType={"button"}
                          buttonIcon={
                            loading
                              ? "fa-light fa-spinner fa-spin"
                              : "fa-light fa-check"
                          }
                          buttonIconPosition={"left"}
                          buttonLabel={
                            confirmButtonLabel ? confirmButtonLabel : "Confirm"
                          }
                          buttonFunction={onSubmit}
                          buttonHasLink={false}
                          buttonDisabled={loading}
                        />
                      </motion.div>
                    </motion.div>
                  </div>
                </motion.div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationPopup;
